import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Input, Checkbox } from 'app/theme/forms'
import { usesScheduler, isMeetingBooked } from '../scheduler'
import HubspotMeetingEmbed from './HubspotMeetingEmbed'

const formatPhone = str => {
  const clean = ('' + str).replace(/\D/g, '');
  const match = clean.match(/^(\d{3})(\d{1,3})?(\d{1,4})?(.*)/)

  if (!match) {
    return clean ? `(${clean}` : ''
  } else if (match[3]) {
    return `(${match[1]}) ${match[2]}-${match[3]}${match[4]}`
  } else if (match[2]) {
    return `(${match[1]}) ${match[2]}`
  } else {
    return `(${match[1]}) `
  }
}

export default function LeadContact(props) {
  const { data, setData, errors, setErrors, nextStep, posthog } = props

  const scheduler = usesScheduler(data)
  const booked = isMeetingBooked(data)

  // Submit only once the booking has landed in `data` — calling nextStep() from
  // the message handler would submit the stale `data` it closed over and drop
  // the contact fields from the payload.
  useEffect(() => {
    if (scheduler && booked) {
      nextStep?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduler, booked])

  const setContact = changes => {
    setData(prev => ({ ...prev, contact: { ...prev.contact, ...changes } }))
  }

  const switchToManualEntry = (reason, { prefill, meeting } = {}) => {
    posthog?.capture('lead_meeting_embed_manual_fallback', { reason })
    setErrors(null)
    setData(prev => ({
      ...prev,
      contact: { ...prev.contact, ...prefill },
      meta: {
        ...prev.meta,
        contact_mode: 'manual',
        // Keep the attempt on the lead, but never as a confirmed booking
        ...(meeting ? { hubspot_meeting: { ...meeting, booked: false } } : {}),
      },
    }))
  }

  if (scheduler) {
    return (
      <div className="p-8 pb-10">
        <div className="grid gap-6 m-auto lg:w-2/3">
          {/* Above the scheduler, not below — the booking auto-submits the lead,
              so a checkbox further down the page would never get ticked. Sticky
              because the calendar is taller than the modal body: once the visitor
              scrolls to a time slot, a static checkbox would be off screen.
              bg-gray-100 matches the modal body (LeadFormModal sets it there). */}
          <div className="sticky top-0 z-10 bg-gray-100">
            <SMSOptIn data={data} setContact={setContact} />
          </div>

          {errors?.scheduler && (
            <p className="m-0 text-sm text-red">{errors.scheduler}</p>
          )}

          <HubspotMeetingEmbed
            contact={data.contact}
            posthog={posthog}
            onBooked={(contact, meeting) => {
              setData(prev => ({
                ...prev,
                contact: { ...prev.contact, ...contact },
                meta: { ...prev.meta, hubspot_meeting: meeting },
              }))
            }}
            onIncomplete={(contact, meeting, reason) => {
              switchToManualEntry(reason, { prefill: contact, meeting })
            }}
            onFailure={() => switchToManualEntry('load_failed')}
          />

          <p className="m-0 text-sm font-light text-center">
            Rather not book a call now?{' '}
            <button
              type="button"
              data-test-id="use-contact-fields"
              className="underline"
              onClick={() => switchToManualEntry('user_choice')}
            >
              Enter your details instead
            </button>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="p-8 pb-10">
        <div className="grid gap-6 m-auto lg:w-2/3">
          <Input
            autoFocus
            data-test-id='first-name'
            value={data.contact.first_name}
            error={errors?.first_name}
            placeholder="First Name"
            classNames={{
              input: 'input-underline',
            }}
            onChange={first_name => {
              setErrors(prev => ({
                ...prev,
                first_name: null,
              }))
              setData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact,
                  first_name,
                }
              }))
            }}
          />
          <Input
            data-test-id='last-name'
            value={data.contact.last_name}
            error={errors?.last_name}
            placeholder="Last Name"
            classNames={{
              input: 'input-underline',
            }}
            onChange={last_name => {
              setErrors(prev => ({
                ...prev,
                last_name: null,
              }))
              setData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact,
                  last_name,
                }
              }))
            }}
          />
          <Input
            type="email"
            data-test-id='email'
            value={data.contact.email}
            error={errors?.email}
            placeholder="Email Address"
            classNames={{
              input: 'input-underline',
            }}
            onChange={email => {
              setErrors(prev => ({
                ...prev,
                email: null,
              }))
              setData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact,
                  email,
                }
              }))
            }}
          />
          <Input
            type="tel"
            data-test-id='phone-number'
            value={data.contact.phone}
            error={errors?.phone}
            placeholder="Phone Number"
            classNames={{
              input: 'input-underline',
            }}
            onChange={phone => {
              if (phone && phone.length > (data.contact.phone || '').length) {
                phone = formatPhone(phone)
              }
              setErrors(prev => ({
                ...prev,
                phone: null,
              }))
              setData(prev => ({
                ...prev,
                contact: {
                  ...prev.contact,
                  phone,
                }
              }))
            }}
          />
          <SMSOptIn data={data} setContact={setContact} />
        </div>
      </div>
    </div>
  )
}

const SMSOptIn = ({ data, setContact }) => (
  <Checkbox
    data-test-id='sms-opt-in'
    value={data.contact.sms_opt_in}
    className={[
      'font-light text-sm',
    ]}
    checkboxClassName='mr-2 pl-6'
    checked={data.contact.sms_opt_in}
    onChange={sms_opt_in => setContact({ sms_opt_in })}
  >
    <SMSLabelText className='text-sm' />
  </Checkbox>
)

const SMSLabelText = ({className}) => {
  const LinkTo = ({href, children}) => <Link to={{ pathname: href }} target='_blank' className='underline'>{children}</Link>

  return (
    <p className={`m-0 p-0 ${className}`}>
      I agree to receive customer care SMS messages from Roaming Hunger at the number provided related to my event request,
      including booking calls and reminders. Msg frequency varies. Msg & data rates may apply. Reply STOP to
      opt-out. Please see <a className='underline' target="_blank" rel="noreferrer" href='https://privacy.cptn.co/privacy-policy/6e1b0402-6e56-453c-9c37-de518dddb75d'>Privacy Policy</a> and{' '}
      <LinkTo href='/pages/terms_and_conditions/'>Terms & Conditions</LinkTo>.
    </p>
  )
}
